/**
 * User Details Modal Component
 * Displays user profile information, site permissions, and action controls.
 */

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Check, X, Mail, Phone, Building2, UserCheck,
  Calendar, Power, Shield, MapPin,
  ShieldCheck, User as UserIcon, Ban,
} from 'lucide-react';
import { UserProfile, UserSitePermission } from '../../lib/api';
import { UserDetailsSiteAccessTab } from './UserDetailsSiteAccessTab';

function Label({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={className}>{children}</div>;
}

interface UserDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  userDetailTab: 'profile' | 'companies';
  setUserDetailTab: (tab: 'profile' | 'companies') => void;
  userSitePermissions: UserSitePermission[];
  savingPermission: number | null;
  /** True when the logged-in user is a Django superuser (may use Super admin switch). */
  isSuperAdmin: boolean;
  actionLoading: boolean;
  isSelf: (userProfile: UserProfile) => boolean;
  handleToggleActive: (userProfile: UserProfile) => void;
  openChangeRoleDialog: (userProfile: UserProfile) => void;
  handleToggleAdmin: (userProfile: UserProfile, isAdmin: boolean) => void;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
  handleApproveUser: (userId: number) => Promise<void>;
  handleRejectUser: (userId: number) => Promise<void>;
  handleSendInvitation: (userProfile: UserProfile) => Promise<void>;
}

export function UserDetailsModal({
  open,
  onOpenChange,
  selectedUser,
  userDetailTab,
  setUserDetailTab,
  userSitePermissions,
  savingPermission,
  isSuperAdmin,
  actionLoading,
  isSelf,
  handleToggleActive,
  openChangeRoleDialog,
  handleToggleAdmin,
  handleToggleSiteLink,
  handleToggleSitePermission,
  handleApproveUser,
  handleRejectUser,
  handleSendInvitation,
}: UserDetailsModalProps) {
  return (
    <Dialog open={open} onOpenChange={(openVal) => {
      onOpenChange(openVal);
      if (!openVal) setUserDetailTab('profile');
    }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            User Details
          </DialogTitle>
          <DialogDescription>
            {selectedUser?.user.full_name} • {selectedUser?.user.email}
          </DialogDescription>
        </DialogHeader>

        {selectedUser && (
          <Tabs value={userDetailTab} onValueChange={(v) => setUserDetailTab(v as 'profile' | 'companies')} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="profile" className="gap-2">
                <UserIcon className="h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="companies" className="gap-2">
                <Building2 className="h-4 w-4" />
                Related Companies
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="flex-1 overflow-y-auto mt-4 space-y-6">
              {/* Status & Actions Bar */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  {/* CR-4551 round 4: surface the "Invite Sent" intermediate
                      state instead of mis-labelling unaccepted invitees as
                      Active in the User Details modal. */}
                  {!selectedUser.user.is_active ? (
                    <Badge variant="secondary">
                      <X className="h-3 w-3 mr-1" />
                      Inactive
                    </Badge>
                  ) : selectedUser.user.invitation_sent && !selectedUser.user.has_joined ? (
                    <Badge variant="outline" className="text-amber-600 border-amber-600">
                      Invite Sent
                    </Badge>
                  ) : (
                    <Badge variant="default" className="bg-green-500">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </Badge>
                  )}
                  {!selectedUser.is_approved && (
                    <Badge variant="outline" className="text-amber-600 border-amber-600">
                      Pending Approval
                    </Badge>
                  )}
                  {selectedUser.role_details?.is_admin_role && (
                    <Badge variant="outline" className="text-blue-600 border-blue-600">
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      Administrator
                    </Badge>
                  )}
                  {selectedUser.user.is_superuser && (
                    <Badge variant="outline" className="text-purple-600 border-purple-600">
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      Super admin
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  {!isSelf(selectedUser) && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleActive(selectedUser)}
                        disabled={actionLoading}
                      >
                        <Power className="h-4 w-4 mr-1" />
                        {selectedUser.user.is_active ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openChangeRoleDialog(selectedUser)}
                      >
                        <Shield className="h-4 w-4 mr-1" />
                        Change Role
                      </Button>
                      {/* CR-5367: Super Admin toggle is only ever applicable
                          to Valuechain (internal Helix) users — Customer and
                          Supplier user details must not show this control,
                          and the caller must already hold Super Admin rights
                          to grant/revoke. The two gates compose so that a
                          Valuechain super admin opening a Customer/Supplier
                          user detail will not see the toggle either. */}
                      {isSuperAdmin && selectedUser.company_type === 'helix' && (
                        <div className="flex items-center gap-2 ml-2 pl-2 border-l">
                          <label htmlFor="user-super-admin-toggle" className="text-sm text-muted-foreground">
                            Super admin
                          </label>
                          <Switch
                            id="user-super-admin-toggle"
                            checked={selectedUser.user.is_superuser || false}
                            onCheckedChange={(checked) => handleToggleAdmin(selectedUser, checked)}
                            disabled={actionLoading}
                            aria-label="Grant or revoke Django superuser and staff"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Full Name</Label>
                  <p className="text-base mt-1">{selectedUser.user.full_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {selectedUser.user.email}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Company</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {selectedUser.company_name}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Role</Label>
                  <p className="text-base mt-1">
                    <Badge variant="outline">{selectedUser.role_name}</Badge>
                  </p>
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4">
                {selectedUser.job_title && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Position</Label>
                    <p className="text-base mt-1">{selectedUser.job_title}</p>
                  </div>
                )}
                {selectedUser.department && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Department</Label>
                    <p className="text-base mt-1">{selectedUser.department}</p>
                  </div>
                )}
                {selectedUser.phone && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Telephone</Label>
                    <p className="text-base mt-1 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {selectedUser.phone}
                    </p>
                  </div>
                )}
              </div>

              {/* Sites Summary */}
              {selectedUser.site_names.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Current Company Access</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedUser.site_names.map((site, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1">
                        <MapPin className="h-3 w-3" />
                        {site}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Last login</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {selectedUser.user.last_login
                      ? new Date(selectedUser.user.last_login).toLocaleString()
                      : 'Never'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Created At</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {new Date(selectedUser.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="companies" className="flex-1 overflow-y-auto mt-4">
              <UserDetailsSiteAccessTab
                userSitePermissions={userSitePermissions}
                savingPermission={savingPermission}
                selectedUser={selectedUser}
                isSelf={isSelf}
                handleToggleSiteLink={handleToggleSiteLink}
                handleToggleSitePermission={handleToggleSitePermission}
              />
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="border-t pt-4 flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {selectedUser && !isSelf(selectedUser) && (
            <Button
              variant="outline"
              disabled={actionLoading}
              onClick={async () => {
                await handleSendInvitation(selectedUser);
              }}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send invitation email
            </Button>
          )}
          {selectedUser && !selectedUser.is_approved && !isSelf(selectedUser) && (
            <>
              <Button
                variant="destructive"
                disabled={actionLoading}
                onClick={async () => {
                  await handleRejectUser(selectedUser.id);
                }}
              >
                <Ban className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                disabled={actionLoading}
                onClick={async () => {
                  await handleApproveUser(selectedUser.id);
                  onOpenChange(false);
                }}
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Approve user
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
